import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { getClinicDetails, deactivateClinic, reactivateClinic } from '../utils/apiCalls';
import DeactivateModal from '../components/DeactivateModal';
import RegisterLocation from '../components/RegisterLocation';
import LocationCard from '../components/LocationCard';
import BackButton from '../components/BackButton';
import OnboardDoctor from '../components/OnboardDoctor';
import OnboardUser from '../components/OnboardUser';
import ClinicAdmins from '../components/ClinicAdmins';

export default function ClinicOverview() {
    const { clinicId } = useParams();
    const { user, token } = useAuth();
    const [clinic, setClinic] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [showAddLocation, setShowAddLocation] = useState(false);
    const [showOnboard, setShowOnboard] = useState(false);
    const [showOnboardUser, setShowOnboardUser] = useState(false);
    const [selectedLocationId, setSelectedLocationId] = useState(null);

    useEffect(() => {
        if (!token) return;
        const fetchData = async () => {
            try {
                setLoading(true);
                const data = await getClinicDetails(clinicId, token);
                setClinic(data);
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();  
    }, [clinicId, token]);

    const refreshClinic = async () => {
        try {
            const updatedClinic = await getClinicDetails(clinicId, token);
            setClinic(updatedClinic);
        } catch (error) {
            console.error('Error refreshing clinic', error);
        }
    };

    const handleDeactivate = async () => {
        try {
            await deactivateClinic(clinicId, token)
            setShowDeactivateModal(false);
            const updatedClinic = await getClinicDetails(clinicId, token);
            setClinic(updatedClinic);
        } catch (error) {
            console.error(error);
        }
    };

    const handleReactivate = async () => {
        try {
            await reactivateClinic(clinicId, token);
            const updatedClinic = await getClinicDetails(clinicId, token);
            setClinic(updatedClinic);
        } catch (error) {
            console.error(error);
        }
    };

    const handleOnboardDoctor = (locationId) => {
        setSelectedLocationId(locationId)
        setShowOnboard(true);
    }

    const handleCreateUser = (locationId) => {
        setSelectedLocationId(locationId);
        setShowOnboardUser(true);
    }



    if (loading) return <div className='p-6 text-center text-gray-500'>loading clinic details</div>
    if (error) return <div className='p-6 text-center text-red-500'>error: {error}</div>

    return (
        <section className='space-y-6'>
            <div className='mb-4 grid grid-cols-1 md:grid-cols-2'>
                <div>
                    <h2 className="text-2xl font-bold mb-1">
                        {clinic?.clinic_name}
                    </h2>
                    <p className="text-gray-700">timezone: {clinic?.timezone}</p>
                    <p className='mb-4'>status: {' '}
                        <span className={`font-semibold ${clinic?.is_active ? "text-green-600" : 'text-red-600'}`}>
                            {clinic?.is_active ? 'active' : 'deactivated'}
                        </span>
                    </p> 
                </div>
                <div className='flex flex-col md:items-end space-y-3'>
                    <div className='md:px-3'>
                        <button disabled={!clinic?.is_active} onClick={() => setShowAddLocation(true)} className={` text-white px-4 py-2 rounded  shadow-md ${clinic?.is_active ? 'voco-gradient-hover' : 'bg-gray-300'}`}>+ add location</button>
                    </div>

                    {/* Deactivate/Reactivate clinic btns */}
                    { user.role === 'super_admin' ? (
                        <div className='md:px-3'>
                            {clinic?.is_active ? (
                                <button onClick={() => setShowDeactivateModal(true)} className='bg-red-600 text-white px-4 py-2 rounded shadow-md hover:bg-red-700'>
                                    deactivate clinic
                                </button>
                            ) : (
                                <button onClick={handleReactivate} className='bg-green-600 text-white px-4 py-2 rounded  shadow-md hover:bg-green-700'>
                                    reactivate clinic
                                </button>
                            )} 

                            {showDeactivateModal && clinic && (
                                <DeactivateModal selectedName={clinic.clinic_name} handleDeactivate={handleDeactivate} setShowModal2={setShowDeactivateModal}/>
                            )}
                        </div>
                    ) : ''}

                </div>
        </div>

        <ClinicAdmins clinicId={clinicId} token={token} user={user.role} onChange={refreshClinic} />

        {/* Locations  */}
        <div className="space-y-4">
            <h3 className="text-xl font-bold">
                locations
            </h3>
            {clinic?.locations?.length > 0 ? (
                clinic.locations.map((location) => (
                    <LocationCard key={location.location_id} location={location} clinicIsActive={clinic.is_active} token={token} onAddDoctor={handleOnboardDoctor} onAddUser={handleCreateUser} onChange={refreshClinic} />
                ))
            ) : (
                <p className="text-gray-500">no locations found</p>
            )}

            <BackButton />
            {showAddLocation && (
                <RegisterLocation clinicId={clinicId} token={token} setCloseModal={setShowAddLocation} onLocationAdded={refreshClinic}/>
            )}

            {showOnboard && (
                <OnboardDoctor isOpen={showOnboard} onClose={() => setShowOnboard(false)} clinicId={clinicId} locationId={selectedLocationId} token={token} onSuccess={refreshClinic} userRole={user.role} isDoctor={true}/>
            )}

            {showOnboardUser && (
                <OnboardUser isOpen={showOnboardUser} onClose={() => setShowOnboardUser(false)} clinicId={clinicId} locationId={selectedLocationId} token={token} onSuccess={refreshClinic} />
            )}

        </div>
    </section>
   

    )

}